import { push } from "connected-react-router";
import { toast } from "react-toastify";
import * as DatasetAPI from "api/DatasetAPI";
import * as FeedbackAPI from "api/FeedbackAPI";
import * as TagAPI from "api/TagAPI";
import { getSelectedAccountType } from "containers/Accounts/reducer";
import { init as initLogin } from "containers/Login/reducer";
import { getIsDatasetsLoading, updateIsDatasetsLoading } from "containers/App/reducer";
import { getAccount } from "containers/Accounts/reducer";
import * as foldersReducer from "containers/Folders/reducer";
import * as paginationReducer from "containers/Pagination/reducer";
import * as galleryReducer from "containers/Gallery/GalleryReducer";
import { filterOtherUsers, getIsMarketplace } from "containers/Marketplace/reducer";
import { updateError } from "containers/Errors/reducer";
import { delay } from "utils/promise";
import { isEnabled } from "utils/featureFlags";
import { getAPIToken } from "utils/request";
import { findDataSourceDetails, DATA_SOURCES } from "containers/SchemaMonitor/reducer";
import { DATA_SOURCES as LINK_DATA_SOURCES } from "components/View/DatasetLink";
import * as adminReducer from "containers/Admin/reducer";
import * as globalReducer from "containers/App/reducer";
import * as loginReducer from "containers/Login/reducer";
import * as accountsReducer from "containers/Accounts/reducer";
import * as blueprintReducer from "containers/Blueprint/reducer";
import * as setupReducer from "containers/Setup/reducer";
import * as viewReducer from "containers/View/reducer";
import * as schemaMonitorReducer from "containers/SchemaMonitor/reducer";
import * as nextReducer from "containers/Next/NextReducer";
import * as lookerSettingsReducer from "containers/LookerSettings/reducer";
import * as chatGPTReducer from "containers/ChatGPT/reducer";
import moment from "moment";
import CSVIcon from "images/csv_white.svg";
import CSVIconGray from "images/csv_light_grey.svg";
import { InsightFactory } from "utils/insight/insights";
import * as RelationUtils from "utils/relation";
import * as Relations from "types/relation";
import { humanize } from "utils/misc";
import { getErrorNotebook } from "./utils";
import { AibleClientFactory } from "@/client/aible/factory";
import { getStringComparator, keysOf, reverseOrder } from "@/utils/collect";
export const UPDATE_BASE = "home/UPDATE_BASE";
export const UPDATE_FIELD = "home/UPDATE_FIELD";
export const UPDATE_FIELD_VALUE = "home/UPDATE_FIELD_VALUE";
export const UPDATE_DATASETS = "home/UPDATE_DATASETS";
export const RESET_DATASETS = "home/RESET_DATASETS";
export const UPDATE_DATASET_OPTIONS = "home/UPDATE_DATASET_OPTIONS";
export const DATA_CHECKS = [
    "Outliers Detection",
    "Missing Values",
    "Rare Values",
    "Unique Values",
    "Problematic Predictors",
    "Duplicate Headers",
    "Empty Rows",
    "Extra Values"
];
export const WIDE_DATASET_COLUMN_COUNT = 500;
export const MAX_ARCS = 25;
export const MAX_USEFUL_VARIABLES = 50;
const initialState = {};
const doUpdateBase = (state, action) => {
    return {
        ...state,
        ...action.data
    };
};
const doUpdateField = (state, action) => {
    return {
        ...state,
        [action.key]: action.data
    };
};
const doUpdateFieldValue = (state, action) => {
    return {
        ...state,
        [action.id]: {
            ...state[action.id],
            [action.key]: action.data
        }
    };
};
const doUpdateDatasetOptions = (state, action) => {
    return {
        ...state,
        [action.id]: {
            ...state[action.id],
            options: {
                ...state[action.id]?.options,
                [action.key]: action.data
            }
        }
    };
};
function homeReducer(state = initialState, action) {
    switch (action.type) {
        case UPDATE_BASE:
            return doUpdateBase(state, action);
        case UPDATE_FIELD:
            return doUpdateField(state, action);
        case UPDATE_FIELD_VALUE:
            return doUpdateFieldValue(state, action);
        case UPDATE_DATASET_OPTIONS:
            return doUpdateDatasetOptions(state, action);
        case RESET_DATASETS:
            return initialState;
        default:
            return state;
    }
}
/* Selectors */
export function getIsLinkDataset() {
    return isEnabled("link-dataset");
}
export function getIsChangeColumnType() {
    return isEnabled("change-column-type");
}
// Get the options key of the dataset
export function getDatasetOptionsSettings(state, id, datasetOverride) {
    const dataset = datasetOverride ? datasetOverride : getDataset(state, id);
    const options = dataset?.options;
    return options;
}
export function getIsForecastReady(state, id, datasetOverride) {
    const dataset = datasetOverride ? datasetOverride : getDataset(state, id);
    const datasetAccount = dataset && dataset.accountId && getAccount(state, dataset.accountId);
    const datasetAccountType = datasetAccount ? datasetAccount.type : null;
    if (!isEnabled("forecasting") || datasetAccountType === "SPARK") {
        return false;
    }
    const options = getForecastOptions(state, id, datasetOverride);
    return options && options.length > 0;
}
export function fetchErrorNotebook(accountId, datasetId) {
    return (dispatch, getState) => {
        const retAccountId = accountId ?? getDatasetAccountId(getState(), datasetId);
        return getErrorNotebook(retAccountId, datasetId);
    };
}
export function getForecastOptions(state, id, datasetOverride) {
    const dataset = datasetOverride ? datasetOverride : getDataset(state, id);
    const outcomeType = getOutcomeType(state, id, datasetOverride);
    if (!dataset || outcomeType !== "number") {
        return [];
    }
    return (dataset &&
        dataset.labels &&
        dataset.labels
            .filter((l) => {
            const currField = dataset.fields && dataset.fields[l];
            return currField && currField.type === "date";
        })
            .map((h) => ({ value: h, label: h })));
}
export function parseWarningForEDG(warning) {
    let retMessage = warning && warning.message;
    if (warning && warning.metadata && warning.metadata.column) {
        const column = warning.metadata.column;
        switch (warning.code) {
            case "MissingValues":
                retMessage =
                    "Column " +
                        column +
                        " has " +
                        (!isNaN(warning.metadata.num_missing) && !isNaN(warning.metadata.percentage)
                            ? warning.metadata.num_missing + "(" + Number(warning.metadata.percentage).toFixed(2) + "%) "
                            : "") +
                        "missing values." +
                        " Explorations and Aible AI modeling will treat them as a separate “missing” category.";
                break;
            case "NoSignalWarning":
                retMessage =
                    "Column " +
                        column +
                        " has too few recurring values. Explorations and Aible AI modeling will ignore this column.";
                break;
            case "HighCardinalityDiscard":
                retMessage =
                    "Column " +
                        column +
                        " has too many values to model. Explorations and Aible AI modeling will ignore this column.";
                break;
            case "HighCardinalityKeep":
                retMessage =
                    "Column " +
                        column +
                        " has too many values to model. Its most informative values would be flagged and included in explorations and Aible AI modeling.";
                break;
        }
    }
    return retMessage;
}
export function getDatasets(state) {
    return state?.datasets;
}
export function getDatasetsArr(state) {
    const datasets = getDatasets(state);
    return keysOf(datasets)
        .map((key) => datasets[key])
        .filter((dataset) => dataset !== undefined)
        .filter((dataset) => dataset && dataset.deleted !== true && dataset.isHidden !== true)
        .sort(reverseOrder(getStringComparator((d) => d.created_date)));
}
export function getDataset(state, id) {
    return getDatasets(state) ? getDatasets(state)[id ?? -1] : undefined;
}
export function getSimilarDatasets(state, id) {
    const dataset = getDataset(state, id);
    const datasets = getDatasetsArr(state);
    if (!dataset) {
        return [];
    }
    return datasets.filter((d) => d.outcome &&
        d.fields &&
        dataset.fields &&
        Object.keys(d.fields).reduce((a, b) => a && dataset.fields[d.fields[b].label] !== undefined, true));
}
export function getDatasetsBySource(state, source) {
    const datasets = getDatasetsArr(state);
    const expectedFields = source.content.schema;
    if (expectedFields === undefined) {
        return datasets;
    }
    const expectedFieldsJson = JSON.stringify(expectedFields);
    return datasets.filter((d) => JSON.stringify(d.source.content.schema) == expectedFieldsJson);
}
export function getDatasetsMatchingTransformations(state, transformations) {
    const relation = transformations.plan?.relation;
    const datasets = getDatasetsArr(state);
    if (relation === undefined) {
        return datasets;
    }
    const sources = RelationUtils.getSources(relation);
}
// Get the label for the place the dataset is stored in
export function getDatasetSource(state, id) {
    const dataset = getDataset(state, id);
    const csvDataSource = DATA_SOURCES.find((source) => source?.value === "CSV");
    let sourceLabel = csvDataSource?.shortLabel ?? csvDataSource?.label;
    const sourceContent = dataset?.source?.content;
    if (sourceContent?.type === "CONNECTION_METADATA") {
        let datasetSource = DATA_SOURCES.find((source) => source?.value === sourceContent?.provider);
        if (datasetSource === undefined) {
            datasetSource = LINK_DATA_SOURCES.find((source) => String(source?.value).toUpperCase() === String(sourceContent?.provider).toUpperCase());
        }
        sourceLabel = datasetSource?.shortLabel ?? datasetSource?.label ?? sourceLabel;
    }
    else {
        const datasetAccountId = getDatasetAccountId(state, id);
        const accountName = accountsReducer.getAccountName(state, datasetAccountId);
        const accountType = accountsReducer.getAccountType(state, datasetAccountId);
        sourceLabel = accountType ?? accountName ?? sourceLabel;
    }
    return sourceLabel;
}
export function getDatasetSourceDisplayFromState(state, id) {
    const data = getDataset(state, id);
    const schemaMonitors = schemaMonitorReducer.getSchemaMonitors(state);
    return getDatasetSourceDisplay(data, schemaMonitors);
}
// Get the icons and label for the dataset source. Defaults to CSV
export function getDatasetSourceDisplay(data, schemaMonitors) {
    const csvDataSource = DATA_SOURCES.find((source) => source?.value === "CSV");
    let datasetIconGray = csvDataSource?.iconGray ?? CSVIconGray;
    let datasetIconWhite = csvDataSource?.iconWhite ?? CSVIcon;
    let datasetTypeLabel = csvDataSource?.shortLabel ?? csvDataSource?.label;
    let datasetType = csvDataSource?.shortLabel ?? csvDataSource?.label;
    const sourceContent = data?.source?.content;
    if (sourceContent?.type === "CONNECTION_METADATA") {
        const datasetSource = DATA_SOURCES.find((source) => source?.value === sourceContent.provider) ??
            LINK_DATA_SOURCES.find((source) => String(source?.value).toUpperCase() === String(sourceContent.provider).toUpperCase());
        datasetIconGray = datasetSource?.iconGray ?? datasetSource?.image ?? datasetIconGray;
        datasetIconWhite = datasetSource?.iconWhite ?? datasetSource?.image ?? datasetIconWhite;
        datasetTypeLabel = datasetSource?.shortLabel ?? datasetSource?.label ?? datasetTypeLabel;
        datasetType = datasetTypeLabel;
    }
    else if (data !== undefined &&
        data.schema_monitor_id !== undefined &&
        schemaMonitors[data.schema_monitor_id] !== undefined) {
        const schemaMonitor = schemaMonitors[data.schema_monitor_id];
        const datasetSource = DATA_SOURCES.find((source) => String(source?.value).toUpperCase() === String(schemaMonitor.type).toUpperCase());
        datasetIconGray = datasetSource?.iconGray ?? datasetSource?.image ?? datasetIconGray;
        datasetIconWhite = datasetSource?.iconWhite ?? datasetSource?.image ?? datasetIconWhite;
        datasetTypeLabel = datasetSource?.shortLabel ?? datasetSource?.label ?? datasetTypeLabel;
        datasetType = datasetTypeLabel;
    }
    return {
        label: datasetTypeLabel,
        iconGray: datasetIconGray,
        iconWhite: datasetIconWhite,
        type: datasetType
    };
}
function checkIsGoogleDataset(dataset, googleSheetsData) {
    // const retName = "Large Sales No Stagename - Sales Optimization"
    let isGoogleDataset = false;
    if (!googleSheetsData?.name || !googleSheetsData?.sheetName) {
        return isGoogleDataset;
    }
    const retName = googleSheetsData?.name + " - " + googleSheetsData?.sheetName;
    if (dataset?.name?.substring(0, retName.length) === retName) {
        isGoogleDataset = true;
    }
    return isGoogleDataset;
}
export function getGoogleSheetsDataset(state) {
    const googleSheetsData = globalReducer.getGoogleSheetsData(state);
    const folderId = foldersReducer.getSelectedFolder(state);
    const dataset = getDatasetsArr(state).find((d) => {
        if (folderId !== d?.folder_id) {
            return false;
        }
        if (checkIsGoogleDataset(d, googleSheetsData)) {
            console.log(d);
            return true;
        }
        return false;
    });
    if (dataset?.id) {
        const datasetId = dataset.id;
        dataset.defaultSuccess = getDefaultSuccess(state, datasetId);
        dataset.datasetSuccessOptions = getSuccessOptions(state, datasetId);
    }
    return dataset;
}
export function createGoogleSheetsDataset() {
    return (dispatch, getState) => {
        const folderId = foldersReducer.getSelectedFolder(getState());
        const authHeader = "Bearer " + getAPIToken();
        const accountId = accountsReducer.getSelectedAccountId(getState());
        const createData = {
            action: "CREATE_DATASET",
            folderId: folderId,
            accountId: accountId,
            authHeader: authHeader
        };
        dispatch(globalReducer.postToGoogleSheets(createData));
    };
}
export function getPermissions(state, id) {
    return getDataset(state, id)?.permissions;
}
export function getUserDeletePermission(state, id) {
    const permissions = getPermissions(state, id);
    return permissions && permissions.can_delete;
}
export function getUserEditPermission(state, id) {
    const permissions = getPermissions(state, id);
    return permissions && permissions.can_edit;
}
export function getUserManagePermissions(state, id) {
    const permissions = getPermissions(state, id);
    return permissions && permissions.can_manage_permissions;
}
export function getLinkedBlueprints(state, id) {
    const blueprints = blueprintReducer.getSortedBlueprints(state) || [];
    return blueprints.filter((bp) => bp && bp.wizard && bp.wizard.dataset && bp.wizard.dataset.id === id);
}
export function getDatasetLabel(state, id) {
    return getDataset(state, id)?.label;
}
export function getDatasetAccountId(state, id) {
    return getDataset(state, id)?.account_id;
}
export function getDatasetOptions(state) {
    const datasets = getDatasets(state);
    const options = [];
    for (const key of keysOf(datasets)) {
        const d = datasets[key];
        if (d !== undefined) {
            options.push({
                value: key,
                label: d.label
            });
        }
    }
    return options;
}
export function getFields(state, id) {
    return getDataset(state, id)?.fields ?? {};
}
export function getFieldsArr(state, id) {
    const fields = getFields(state, id);
    return fields ? Object.keys(fields).map((key) => fields[key]) : [];
}
export function getAnalyzedOutcomes(state, id) {
    return getFieldsArr(state, id)
        .filter((field) => field && (field.inclusionProbabilities || field.inclusionProbabilitiesWarning))
        .map((field) => field && field.label);
}
export function getAiReadyOutcomes(state, id) {
    return getAnalyzedOutcomes(state, id).filter((outcome) => {
        const aiMetrics = setupReducer.getAiReadyMetrics(state, undefined, id, outcome);
        return aiMetrics?.isAiReady;
    });
}
export function getStatCharts(state) {
    const users = adminReducer.getUsers(state);
    const dailyUsers = users !== undefined && users.length > 0 ? users.length : "";
    let rowsPerWeek = undefined;
    let datasetsPerWeek = undefined;
    const dailyCost = 5;
    const dataOvertime = [];
    const datasets = getDatasetsArr(state);
    if (users !== undefined && users.length > 0) {
        const firstUser = users.reduce((a, b) => {
            const aCreated = a.invite?.created_date ?? a.created_date;
            const bCreated = b.invite?.created_date ?? b.created_date;
            return aCreated < bCreated ? a : b;
        }, users[0]);
        const firstUserCreatedDate = firstUser?.invite?.created_date ?? firstUser?.created_date;
        const numOfWeeks = firstUser ? moment().diff(moment(firstUserCreatedDate), "week") : 0;
        for (let i = 0; i < numOfWeeks; i++) {
            const cutoffTimeStart = moment(firstUserCreatedDate).add(i, "week");
            const cutOffTimeEnd = moment(firstUserCreatedDate).add(i + 1, "week");
            const createdUsers = users.filter((d) => {
                const createdDate = d.invite?.created_date ?? d.created_date;
                return moment(createdDate) >= cutoffTimeStart && moment(createdDate) <= cutOffTimeEnd;
            });
            const createdDatasets = datasets.filter((d) => moment(d.created_date) >= cutoffTimeStart && moment(d.created_date) <= cutOffTimeEnd);
            dataOvertime.push({
                name: cutoffTimeStart.format("MMM D YYYY"),
                createdDatasets: createdUsers,
                ["New Users"]: createdUsers.length,
                ["New Datasets"]: createdDatasets.length,
                ["New Rows"]: createdDatasets.reduce((a, b) => a + b.rowCount, 0)
            });
        }
        if (numOfWeeks === 0) {
            datasetsPerWeek = 0;
            rowsPerWeek = 0;
        }
        else if (datasets && datasets.length > 0) {
            datasetsPerWeek = datasets.length / numOfWeeks;
            rowsPerWeek = datasets.reduce((a, b) => a + b.rowCount, 0) / numOfWeeks;
        }
    }
    return {
        dailyUsers: dailyUsers,
        rowsPerWeek: rowsPerWeek,
        datasetsPerWeek: datasetsPerWeek,
        dailyCost: dailyCost,
        dataOvertime: dataOvertime
    };
}
export function getLinkedSchemaMonitorId(state, id) {
    const dataset = getDataset(state, id);
    return dataset && dataset.schema_monitor_id;
}
export function getLinkedSchemaMonitor(state, id) {
    const monitorId = getLinkedSchemaMonitorId(state, id);
    if (monitorId === undefined) {
        return undefined;
    }
    return schemaMonitorReducer.getSchemaMonitor(state, monitorId);
}
export function getLinkedSchemaMonitorTable(state, id) {
    const dataset = getDataset(state, id);
    if (dataset && dataset.source && dataset.source.content.type == "CONNECTION_METADATA") {
        return dataset.source.content.table;
    }
    return undefined;
}
export function getLinkedSchemaChanges(state, id) {
    const sMonitorId = getLinkedSchemaMonitorId(state, id);
    if (sMonitorId) {
        return undefined;
    }
    else {
        return undefined;
    }
}
export function getIsLinkedSchemaChanged(state, id) {
    const hasChanges = getLinkedSchemaChanges(state, id);
    if (!hasChanges) {
        return false;
    }
    const sMonitor = getLinkedSchemaMonitor(state, id);
    const dataset = getDataset(state, id);
    const monitorUpdate = sMonitor?.updated_date;
    const datasetDate = dataset?.created_date;
    return moment(datasetDate).isBefore(moment(monitorUpdate));
}
export function selectAndOpenMonitorSchema(datasetId) {
    return (dispatch, getState) => {
        const state = getState();
        const sMonitorId = getLinkedSchemaMonitorId(state, datasetId);
        if (sMonitorId) {
            const tableName = getLinkedSchemaMonitorTable(state, datasetId);
            if (tableName === undefined) {
                return undefined;
            }
            const table = schemaMonitorReducer.getSchemaMonitorTable(state, sMonitorId, tableName);
            if (table === undefined) {
                return undefined;
            }
            dispatch(schemaMonitorReducer.selectSchemaMonitor(sMonitorId));
            dispatch(schemaMonitorReducer.selectTable(sMonitorId, table));
            dispatch(schemaMonitorReducer.updateIsShowModal(true));
        }
        else {
            return undefined;
        }
    };
}
export function getRowCount(state, id) {
    return getDataset(state, id)?.rowCount;
}
export function getDescriptions(state, id) {
    return getDataset(state, id)?.descriptions;
}
export function getProcessingDatasets(state) {
    return getDatasetsArr(state).filter((dataset) => (dataset.rowCount === -1 &&
        dataset.errors.length === 0 &&
        dataset.status &&
        dataset.status.indexOf("missing_target_variable") === -1) ||
        dataset.stack_id);
}
export function isLoading(state, id) {
    const dataset = getDataset(state, id);
    if (dataset === undefined) {
        return false;
    }
    const noOutcome = dataset.status && dataset.status.indexOf("missing_target_variable") !== -1;
    const finishedIndexing = dataset.status &&
        dataset.status.indexOf("indexing") === -1 &&
        dataset.status.indexOf("finishing_indexing") === -1 &&
        !dataset.stack_id;
    const isError = dataset.errors && dataset.errors.length > 0;
    const isDone = dataset.rowCount !== -1 || (finishedIndexing && noOutcome);
    return (!isDone && !isError) || dataset.stack_id !== undefined;
}
export function getFieldType(state, id, fieldId) {
    return getFields(state, id)[fieldId] ? getFields(state, id)[fieldId].type : undefined;
}
export function getOutcome(state, id) {
    return getDataset(state, id)?.outcome;
}
export function getOutcomeOptions(state, id) {
    const dataset = getDataset(state, id);
    if (dataset === undefined) {
        return [];
    }
    const relation = RelationUtils.fromDataset(dataset);
    const context = RelationUtils.getOutputContext(relation);
    return RelationUtils.getOutcomeOptionsFromOutputContext(context);
}
export function getOriginalOutcome(state, id) {
    return getDataset(state, id)?.originalOutcome;
}
export function getOutcomeType(state, id, datasetOverride, outcomeOverride) {
    const dataset = datasetOverride ? datasetOverride : getDataset(state, id);
    const outcome = (outcomeOverride ? outcomeOverride : dataset?.outcome) ?? "";
    return dataset && dataset.fields && dataset.fields[outcome] ? dataset.fields[outcome].type : "number";
}
export function getIsOutcomeMulticategorical(state, id, datasetOverride, outcomeOverride) {
    const dataset = datasetOverride ? datasetOverride : getDataset(state, id);
    const outcome = (outcomeOverride ? outcomeOverride : dataset?.outcome) ?? "";
    const relation = RelationUtils.fromDataset(dataset);
    const context = RelationUtils.getOutputContext(relation);
    const output = context[outcome];
    return output?.type === "TEXT" && (output?.values?.length ?? 0) > 2;
}
export function getIsShellDataset(state, id) {
    const dataset = getDataset(state, id);
    if (dataset === undefined) {
        return false;
    }
    return (dataset.status_name == "NOT_STARTED" &&
        dataset.labels.length == 0 &&
        (dataset.stack_id ?? "").startsWith("00000000") &&
        dataset.source?.content?.type === "RAW_DATA");
}
export function getDefaultSuccess(state, id, datasetOverride) {
    const dataset = datasetOverride ? datasetOverride : getDataset(state, id);
    const outcomeValues = dataset?.fields?.[dataset.outcome ?? ""]?.data;
    const targetValue = outcomeValues?.find((v) => {
        return dataset?.target_value?.includes(v?.rawValue) || dataset?.target_value?.includes(v?.value);
    });
    return targetValue?.value;
}
export function getSuccessOptions(state, id, datasetOverride, outcomeOverride) {
    const dataset = datasetOverride ? datasetOverride : getDataset(state, id);
    const outcome = (outcomeOverride ? outcomeOverride : dataset?.outcome) ?? "";
    return dataset && dataset.fields && dataset.fields[outcome] && dataset.fields[outcome].data
        ? dataset.fields[outcome].data
            .map((v) => ({
            value: v.value,
            rawValue: v.rawValue,
            label: v.value,
            count: v.count
        }))
            .filter((f) => f.value !== "{Missing}" && f.value !== "{Other}")
        : [];
}
export function getColumnOptions(state, id) {
    const fields = getFieldsArr(state, id);
    return fields
        ? fields.filter((field) => field.type === "number").map((field) => ({ value: field.label, label: field.label }))
        : [];
}
export function getSpecialOptions(state, id) {
    const fields = getFieldsArr(state, id);
    const retOptions = [];
    for (const field of fields.filter((f) => f.type === "categorical")) {
        if (field.data !== undefined) {
            for (const value of field.data) {
                retOptions.push({
                    label: field.label + " - " + value.value,
                    value: field.label + " - " + value.value,
                    field: field.label,
                    fieldValue: value.value
                });
            }
        }
    }
    return retOptions;
}
export function getDatasetUrl(state, id) {
    const dataset = getDataset(state, id);
    const rawUrl = dataset && dataset.source && dataset.source.url;
    if (!rawUrl) {
        return undefined;
    }
    const bucket = rawUrl ? rawUrl.substring(5, rawUrl.length) : undefined;
    const url = "https://s3.console.aws.amazon.com/s3/buckets/" + bucket + "/?region=us-east-1&tab=overview";
    return url;
}
export function getCustomModelURL(state) {
    const datasets = getDatasetsArr(state);
    const sourceUrl = datasets.map((d) => d.source.url ?? "").filter((url) => url.length > 0)[0];
    const bucket = sourceUrl ? sourceUrl.substring(5, sourceUrl.indexOf("uploads/")) : undefined;
    const url = "https://s3.console.aws.amazon.com/s3/buckets/" + bucket + "custom-models/?region=us-east-1&tab=overview";
    return url;
}
export function getInteractions() {
    // legacy feature, to be removed
    return [];
}
export function getRankedFields(state, id) {
    const dataset = getDataset(state, id);
    if (dataset === undefined) {
        return [];
    }
    const fields = dataset.ranked_fields ?? Object.values(dataset.fields);
    return fields;
}
export function getIsExploreReady(state, id) {
    const pivot_tables = getPivotTables(state, id);
    return pivot_tables !== undefined && pivot_tables.tables.length > 0;
}
export function getPivotTables(state, id) {
    const base = getDatasets(state);
    return base[id]?.pivot_tables;
}
export function getInsightFactory(state, id, job, successValue, comparisonOptions, minimumValueCount) {
    const dataset = getDataset(state, id);
    if (dataset === undefined) {
        return undefined;
    }
    const pivot_tables = getPivotTables(state, id);
    if (pivot_tables === undefined) {
        return undefined;
    }
    const ip = dataset.fields[dataset.outcome ?? ""]?.inclusionProbabilities ?? [];
    const iip = dataset.interaction_scores ?? [];
    const successOverride = successValue === undefined
        ? undefined
        : {
            name: pivot_tables.target.name,
            success_value: successValue
        };
    const exploreOptions = {};
    if (minimumValueCount !== undefined) {
        exploreOptions.minSampleSize = minimumValueCount;
    }
    const maxOrder = dataset.options?.target_model_params?.data?.combination_order;
    if (maxOrder !== undefined) {
        exploreOptions.maxOrder = maxOrder;
    }
    const factory = InsightFactory.create(dataset, pivot_tables, ip, iip, job, successOverride, comparisonOptions, exploreOptions);
    return factory;
}
/* Actions */
export function updateDataset(id, dataset) {
    return { type: UPDATE_FIELD, key: id, data: dataset };
}
export function updatePivotTables(id, pivotTables) {
    return { type: UPDATE_FIELD_VALUE, id: id, key: "pivot_tables", data: pivotTables };
}
export function updateIsSavingKPISettings(id, value) {
    return { type: UPDATE_FIELD_VALUE, id: id, key: "isSavingKPISettings", data: value };
}
export function resetDatasets() {
    return { type: RESET_DATASETS };
}
export function changeDatasetName(id, name) {
    return (dispatch) => {
        return DatasetAPI.renameDataset(id, name).then((response) => {
            return dispatch(updateDataset(response.id, transformDataset(response)));
        });
    };
}
export function updateDatasets(datasets) {
    return { type: UPDATE_BASE, data: datasets };
}
export function updateDatasetOptionsSettings(id, key, data) {
    return { type: UPDATE_DATASET_OPTIONS, id: id, key: key, data: data };
}
export function changeOutcome(id, outcome) {
    return (dispatch, getState) => {
        let dataset = getDataset(getState(), id);
        if (dataset) {
            const originalOutcome = dataset.outcome;
            dataset = {
                ...dataset,
                outcome: outcome
            };
            if (!dataset.originalOutcome) {
                dataset.originalOutcome = originalOutcome;
            }
        }
        if (dataset !== undefined) {
            dispatch(updateDataset(dataset.id, dataset));
        }
        const viewField = galleryReducer.getViewField(getState(), id);
        if (viewField && dataset?.fields[viewField.label]) {
            dispatch(galleryReducer.updateViewField(id, dataset?.fields[viewField.label], true));
        }
        return Promise.resolve();
    };
}
export function transformDataset(dataset, checkExtraWide = false) {
    const retDataset = {
        ...dataset,
        id: dataset.id,
        accountId: dataset.account_id,
        label: dataset.name,
        fields: {},
        rowCount: dataset.number_of_rows,
        observedRowCount: dataset.number_of_rows,
        created: dataset.created_date,
        updated_date: dataset.updated_date,
        created_by: dataset.created_by,
        // We have training_data.status which can be NOT_STARTED, RUNNING, DONE, ERROR
        // also have overall_status, the old inferred status field.
        dataset_status: dataset.status,
        stack_id: dataset.stack_id,
        status_name: dataset.status,
        status: dataset.overall_status,
        errors: dataset.annotations ? dataset.annotations.filter((annotation) => annotation.type === "error") : [],
        // Remove scope !== date after demo
        warnings: dataset.annotations
            ? dataset.annotations
                .filter((annotation) => annotation.type === "warning")
                .filter((annotation) => annotation.scope !== "date")
            : [],
        annotations: dataset.annotations ? dataset.annotations.filter((annotation) => annotation.type === "info") : [],
        outcome: dataset.target_index !== undefined && dataset.target_index !== null
            ? dataset.labels[dataset.target_index]
            : undefined,
        sourceUrl: dataset.source.url,
        deleted: dataset.deleted === "DELETED",
        descriptions: dataset.descriptions,
        pendingTransformations: dataset.transformations,
        ranked_fields: [],
        suggested_features: [],
        interaction_scores: [],
        comments: [],
        ratings: [],
        tags: []
    };
    const plan = dataset.transformations.plan;
    if (plan?.relation !== undefined) {
        plan.relation = RelationUtils.simplify(plan.relation);
    }
    else {
        const relation = Relations.readDataSource(dataset.source);
        if (retDataset.transformations === undefined) {
            retDataset.transformations = {
                plan: { relation }
            };
        }
        if (retDataset.transformations.plan === undefined) {
            retDataset.transformations.plan = { relation };
        }
    }
    if (checkExtraWide && dataset.labels?.length > WIDE_DATASET_COLUMN_COUNT && retDataset.outcome !== undefined) {
        retDataset.wideDatasetWarning = true;
        retDataset.wideDatasetColumnCount = dataset.labels?.length;
        const outcome = retDataset.outcome;
        const outcomeIndex = dataset.labels.findIndex((l) => l === outcome);
        const retMapping = dataset.mappings?.[outcomeIndex];
        if (retMapping?.[2]) {
            retMapping[2] = {
                missing: retMapping[2].missing,
                num_unique_val: retMapping[2].num_unique_val,
                num_unique_val_to_rows: retMapping[2].num_unique_val_to_rows
            };
        }
        retDataset.mappings = [retMapping];
        retDataset.descriptions = [dataset.descriptions?.[outcomeIndex]];
        retDataset.labels = [outcome];
        retDataset.categorical_counts = [];
        const outcomeCatCounts = dataset.categorical_counts?.find((c) => c[0] === outcome);
        if (outcomeCatCounts) {
            retDataset.categorical_counts.push(outcomeCatCounts);
        }
        dataset.mappings = retDataset.mappings;
        dataset.descriptions = retDataset.descriptions;
        dataset.labels = retDataset.labels;
        dataset.categorical_counts = retDataset.categorical_counts;
    }
    for (const [index, value] of (dataset.labels || []).entries()) {
        const field = {
            label: value,
            ignored: false,
            type: dataset.mappings && dataset.mappings[index] ? dataset.mappings[index][0] : undefined,
            warnings: retDataset.warnings.filter((w) => w.scope === value || (w.metadata && w.metadata.column === value)),
            description: dataset.descriptions ? dataset.descriptions[index] : undefined,
            index: index
        };
        retDataset.fields[value] = field;
        // if (retDataset.fields[value].warnings && retDataset.fields[value].warnings.length) {
        // 	for (let w of retDataset.fields[value].warnings) {
        // 		if (w.message && w.message.includes("It will be discarded.")) {
        // 			retDataset.fields[value].ignored = true;
        // 		}
        // 	}
        // }
        const mapping = dataset?.mappings?.[index];
        if (!mapping || mapping.length !== 3) {
            continue;
        }
        const colType = mapping[0];
        const colValues = mapping[1];
        const colMetadata = mapping[2];
        if (field.type === "number") {
            field.min = Number(colValues[0]);
            field.max = Number(colValues[1]);
            field.mean = mapping[2]["mean"];
            field.isNumeric = true;
        }
        else if (colType == "categorical") {
            const possibleNumerics = colValues?.filter((x) => x != "{Missing}") ?? [];
            field.isNumeric = possibleNumerics.length > 0 && possibleNumerics.every((x) => typeof x === "number");
        }
        if ((field.type === "number" || field.type === "date") && colMetadata.min !== undefined) {
            field.min = colMetadata.min;
        }
        if ((field.type === "number" || field.type === "date") && colMetadata.max !== undefined) {
            field.max = colMetadata.max;
        }
        if (colType === "categorical" && colMetadata.min !== undefined && colMetadata.min !== undefined) {
            field.type = "date";
            field.isNumeric = false;
        }
        if (field.type === "number" && colMetadata?.["Abs Correlation"]) {
            field.correlation = colMetadata["Abs Correlation"];
        }
        if (colMetadata.leakage_info) {
            field.leakage_info = colMetadata.leakage_info;
        }
        if (colMetadata?.["suggested_features"]) {
            retDataset.suggested_features = colMetadata["suggested_features"];
            retDataset.suggested_features_count = Object.values(retDataset.suggested_features ?? {})
                .filter((x) => (x?.choices ?? []).length > 0)
                .map((x) => (x.choice_type == "OneOf" ? 1 : x.choices.length))
                .reduce((x, y) => x + y, 0);
        }
        if (colMetadata?.["inclusion_probabilities"]) {
            const rawInclusionProbabilities = colMetadata["inclusion_probabilities"];
            if (Array.isArray(rawInclusionProbabilities)) {
                field.inclusionProbabilities = rawInclusionProbabilities;
            }
            else {
                field.inclusionProbabilities = Object.keys(rawInclusionProbabilities).map((key) => ({
                    keys: [key],
                    value: rawInclusionProbabilities[key]
                }));
            }
        }
        if (colMetadata?.["interaction_inclusion_probabilities"]) {
            const probs = colMetadata["interaction_inclusion_probabilities"];
            const firstOrders = [];
            const secondOrders = [];
            if (Array.isArray(probs)) {
                probs.map((prob) => {
                    if (prob?.keys) {
                        if (prob.keys.length > 1) {
                            secondOrders.push(prob);
                        }
                        else {
                            firstOrders.push(prob);
                        }
                    }
                });
            }
            else {
                Object.entries(probs).map(([key, val]) => {
                    const split = key.split(":");
                    if (split.length > 1) {
                        secondOrders.push({
                            keys: split,
                            value: val
                        });
                    }
                    else {
                        firstOrders.push({
                            keys: split,
                            value: val
                        });
                    }
                });
            }
            if (firstOrders.length > 0) {
                field.inclusionProbabilities = firstOrders;
            }
            field.interactionInclusionProbabilities = secondOrders;
        }
        if (colMetadata?.["inclusion_probabilities_warning"]) {
            field.inclusionProbabilitiesWarning = colMetadata["inclusion_probabilities_warning"];
        }
        if (field.type == "number" && colMetadata?.quantile_values !== undefined) {
            const quantile_levels = colMetadata.quantile_levels;
            const rawqvalues = colMetadata.quantile_values.map(humanize);
            let n = 20;
            if (quantile_levels.filter((v, i) => (quantile_levels[i] * n) % 1 == 0).length < n + 1) {
                n = 10;
            }
            const mask = rawqvalues.map((v, i) => (i == rawqvalues.length - 1 || rawqvalues[i + 1] > rawqvalues[i]) && (quantile_levels[i] * n) % 1 == 0);
            const fieldMin = rawqvalues[0];
            const fieldMax = rawqvalues[rawqvalues.length - 1];
            const qvalues = rawqvalues.filter((v, i) => mask[i]);
            const qlevels = quantile_levels.filter((v, i) => mask[i]);
            field.isNumeric = true;
            field.data = qvalues
                .map((v, i) => {
                let left = qvalues[i - 1];
                let right = qvalues[i];
                if (left == right) {
                    left = qvalues[i - 1];
                    right = qvalues[i];
                }
                return {
                    min: left,
                    max: right,
                    count: Math.floor(qlevels[i] * dataset.number_of_rows) -
                        Math.floor((qlevels[i - 1] ?? 0) * dataset.number_of_rows),
                    cumCount: Math.floor(qlevels[i] * dataset.number_of_rows),
                    percentile: qlevels[i],
                    value: `${left} to ${right}`,
                    updatedValue: qvalues[i - 1] == qvalues[i]
                        ? `${left}`
                        : qvalues[i - 1] == fieldMin
                            ? `>=${left} to ${right}`
                            : `>${left} to ${right}`
                };
            })
                .filter((v, i) => i > 0);
            const histogram_bins = colMetadata.histogram_bins;
            const histogram_counts = colMetadata.histogram_counts;
            field.histogram_bins = (histogram_bins || [])
                .filter((bin, index) => index < histogram_bins.length - 1)
                .map((bin, index) => {
                const min = humanize(bin);
                const max = humanize(histogram_bins[index + 1]);
                return {
                    value: min === -Infinity
                        ? "Less than " + max
                        : max === +Infinity
                            ? "Greater than " + min
                            : min + ` to ${max < fieldMax ? "<" : ""}` + max,
                    count: histogram_counts[index],
                    min: min,
                    max: max
                };
            })
                .sort((a, b) => (a.min < b.min ? -1 : a.min > b.min ? 1 : 0));
        }
    }
    for (const cat in dataset.categorical_counts) {
        const category = dataset.categorical_counts[cat];
        retDataset.fields[category[0]].data = category[1].map((fieldValue) => {
            return {
                value: String(fieldValue[0]),
                rawValue: fieldValue[0],
                count: fieldValue[1]
            };
        });
    }
    const metadata = dataset.metadata;
    // USED FOR LEGACY DATASET - DO NOT REMOVE
    if (metadata?.univariate_summaries !== undefined) {
        Object.keys(metadata.univariate_summaries).forEach((f) => {
            const mSummary = metadata.univariate_summaries[f];
            if (retDataset.observedRowCount !== undefined &&
                retDataset.observedRowCount <= 0 &&
                mSummary?.number_observed !== undefined &&
                mSummary?.number_missing !== undefined &&
                retDataset.status_name === "DONE") {
                retDataset.observedRowCount = mSummary?.number_observed + mSummary?.number_missing;
                retDataset.number_of_rows = retDataset.observedRowCount;
            }
            const field = retDataset.fields[f];
            if (metadata.univariate_summaries[f].type === "NumericSummary" && field?.type == "number") {
                const summary = metadata.univariate_summaries[f];
                const rawqvalues = summary.quantile_values.map(humanize);
                let n = 20;
                if (summary.quantile_levels.filter((v, i) => (summary.quantile_levels[i] * n) % 1 == 0).length < n + 1) {
                    n = 10;
                }
                const mask = rawqvalues.map((v, i) => (i == rawqvalues.length - 1 || rawqvalues[i + 1] > rawqvalues[i]) &&
                    (summary.quantile_levels[i] * n) % 1 == 0);
                const fieldMin = rawqvalues[0];
                const fieldMax = rawqvalues[rawqvalues.length - 1];
                const qvalues = rawqvalues.filter((v, i) => mask[i]);
                const qlevels = summary.quantile_levels.filter((v, i) => mask[i]);
                field.isNumeric = true;
                field.data = qvalues
                    .map((v, i) => {
                    let left = qvalues[i - 1];
                    let right = qvalues[i];
                    if (left == right) {
                        left = qvalues[i - 1];
                        right = qvalues[i];
                    }
                    return {
                        min: left,
                        max: right,
                        count: Math.floor(qlevels[i] * summary.number_observed) -
                            Math.floor((qlevels[i - 1] ?? 0) * summary.number_observed),
                        cumCount: Math.floor(qlevels[i] * summary.number_observed),
                        percentile: qlevels[i],
                        value: `${left} to ${right}`,
                        updatedValue: qvalues[i - 1] == qvalues[i]
                            ? `${left}`
                            : qvalues[i - 1] == fieldMin
                                ? `>=${left} to ${right}`
                                : `>${left} to ${right}`
                    };
                })
                    .filter((v, i) => i > 0);
                field.histogram_bins = (summary.histogram_bins || [])
                    .filter((bin, index) => index < summary.histogram_bins.length - 1)
                    .map((bin, index) => {
                    const min = humanize(bin);
                    const max = humanize(summary.histogram_bins[index + 1]);
                    return {
                        value: min === -Infinity
                            ? "Less than " + max
                            : max === +Infinity
                                ? "Greater than " + min
                                : min + ` to ${max < fieldMax ? "<" : ""}` + max,
                        count: summary.histogram_counts[index],
                        min: min,
                        max: max
                    };
                })
                    .sort((a, b) => (a.min < b.min ? -1 : a.min > b.min ? 1 : 0));
            }
            else if (metadata.univariate_summaries[f].type === "DateTimeSummary" &&
                metadata.univariate_summaries[f].intensity &&
                retDataset.fields[f]) {
                const currIntensity = metadata.univariate_summaries[f].intensity;
                const iValuesTarget = currIntensity.index_values
                    ? currIntensity.index_values
                    : currIntensity.index && currIntensity.index.index_values
                        ? currIntensity.index.index_values
                        : undefined;
                const valuesTarget = currIntensity.values
                    ? currIntensity.values
                    : currIntensity.data
                        ? currIntensity.data
                        : undefined;
                retDataset.fields[f].type = "date";
                retDataset.fields[f].isNumeric = false;
                retDataset.fields[f].data =
                    iValuesTarget &&
                        iValuesTarget
                            .map((c, k) => ({
                            value: String(c),
                            count: valuesTarget && valuesTarget[k]
                        }))
                            .sort((a, b) => (a.value < b.value ? -1 : a.value > b.value ? 1 : 0));
            }
        });
    }
    for (let index = 0; index < dataset.labels.length; index++) {
        const mapping = dataset.mappings[index] ?? [];
        if (mapping.length == 3 && mapping[2].inclusion_probabilities) {
            retDataset.interaction_model_metrics = mapping[2]["interaction_model_metrics"];
            retDataset.interaction_model_metrics_test = mapping[2]["interaction_model_metrics_test"];
            if (dataset?.labels?.[index] === dataset?.target) {
                if (mapping[2].interaction_scores) {
                    const rawInteractionScores = mapping[2].interaction_scores;
                    if (Array.isArray(rawInteractionScores)) {
                        retDataset["interaction_scores"] = rawInteractionScores;
                    }
                    else {
                        const parsedInteractionScores = [];
                        if (rawInteractionScores) {
                            Object.keys(rawInteractionScores).forEach((key) => {
                                const keys = key.split(":");
                                const value = rawInteractionScores[key];
                                parsedInteractionScores.push({
                                    keys: keys,
                                    value: value
                                });
                            });
                        }
                        retDataset.interaction_scores = parsedInteractionScores;
                    }
                }
                retDataset.interaction_scores = retDataset.interaction_scores
                    .filter((x) => x.value > 0.0)
                    .sort((a, b) => b.value - a.value);
                if (retDataset?.options?.target_steps?.find((x) => x == "SHAP_EXPLORE") !== undefined) {
                    const maxScore = retDataset["interaction_scores"].reduce((currMax, { value }) => Math.max(currMax, value), 0);
                    retDataset.fields[dataset.target].inclusionProbabilities = retDataset["interaction_scores"]
                        .filter((x) => x.keys[0] == x.keys[1])
                        .map((x) => ({
                        value: x.value / maxScore,
                        keys: [x.keys[0]]
                    }));
                    retDataset.fields[dataset.target].interactionInclusionProbabilities = retDataset["interaction_scores"]
                        .filter((x) => x.keys[0] != x.keys[1])
                        .map((x) => ({
                        ...x,
                        value: x.value / maxScore
                    }));
                }
            }
        }
    }
    const fields = Object.values(retDataset.fields).slice();
    const data = retDataset["interaction_scores"];
    if (data !== undefined) {
        const fieldScores = data.reduce((s, x) => {
            s[x.keys[0]] = (s[x.keys[0]] ?? 0) + x.value;
            if (x.keys[0] != x.keys[1]) {
                s[x.keys[1]] = (s[x.keys[1]] ?? 0) + x.value;
            }
            return s;
        }, {});
        for (const label of retDataset.labels) {
            fieldScores[label] ??= 0;
        }
        retDataset.interaction_scores_lookup = data.reduce((l, x) => {
            (l[x.keys[0]] ??= {})[x.keys[1]] = x.value;
            (l[x.keys[1]] ??= {})[x.keys[0]] = x.value;
            return l;
        }, {});
        retDataset.field_scores = fieldScores;
        retDataset.ranked_fields = fields
            .sort((a, b) => fieldScores[b.label] - fieldScores[a.label])
            .map((f, i) => ({ ...f, rank: i }));
    }
    const retSteps = retDataset.options?.target_steps ?? [];
    if (isEnabled("light-gbm-shap-explore") && !retDataset.outcome && !retSteps.includes("SHAP_EXPLORE")) {
        retSteps.push("SHAP_EXPLORE");
        retDataset.options = {
            ...retDataset.options,
            target_steps: retSteps
        };
    }
    if (dataset.status != "ERROR") {
        retDataset.schemaItems = RelationUtils.getSchemaItemsFromDataset(retDataset);
    }
    return retDataset;
}
export function transformDatasets(state, datasets, skipWideDatasetCheck = false) {
    const currentDatasets = { ...getDatasets(state) };
    for (const dataset of datasets) {
        try {
            currentDatasets[dataset.id] = transformDataset(dataset, !skipWideDatasetCheck);
            // const retDataset = injectFeedback(dataset, currentDatasets[dataset.id])
        }
        catch (e) {
            console.log(e); // don't kill all datasets if one fails
        }
    }
    return currentDatasets;
}
export function transformUpdateDataset(id, dataset) {
    return {
        type: UPDATE_FIELD,
        key: dataset.id,
        data: transformDataset(dataset)
    };
}
export function addMockCatalog() {
    return (dispatch, getState) => {
        const dataset = getDataset(getState(), 132);
        if (dataset === undefined) {
            throw Error("INVALID DATASET");
        }
        return dispatch(updateDataset(1515, {
            ...dataset,
            id: 132,
            isMockedCatalog: true
        }));
    };
}
// Placeholder for parsing function
function parseDataForExport(data) {
    return data;
}
export function fetchDatasetJSON(datasetId) {
    return (dispatch) => {
        if (datasetId) {
            return dispatch(fetchDataset(datasetId, true)).then((data) => {
                const parsedData = parseDataForExport(data);
                return JSON.stringify(parsedData);
            });
        }
        return dispatch(fetchNextDatasets(true, true, true)).then((dataArr) => {
            const parsedDataArr = Object.values(dataArr).map(parseDataForExport);
            return JSON.stringify(parsedDataArr);
        });
    };
}
export function fetchDatasetsWithoutDetails(datasetIds) {
    return (dispatch, getState) => {
        const folderId = foldersReducer.getSelectedFolder(getState());
        return DatasetAPI.getDatasetsById(datasetIds, folderId).then((data) => {
            if (folderId !== foldersReducer.getSelectedFolder(getState())) {
                return;
            }
            dispatch(updateDatasets(transformDatasets(getState(), data, true)));
            return;
        });
    };
}
export function fetchDatasetWithoutDetails(datasetId) {
    return (dispatch, getState) => {
        const datasetIds = [datasetId];
        const folderId = foldersReducer.getSelectedFolder(getState());
        return DatasetAPI.getDatasetsById(datasetIds, folderId).then((data) => {
            if (folderId !== foldersReducer.getSelectedFolder(getState())) {
                return;
            }
            dispatch(updateDatasets(transformDatasets(getState(), data, true)));
            return;
        });
    };
}
export function awaitDatasetProcess(datasetId, retries = 0, maxRetries = 10) {
    return (dispatch, getState) => {
        const MAX_RETRIES = maxRetries || 5;
        const dataset = getDataset(getState(), datasetId);
        if (dataset?.status_name === "ERROR" || (retries >= MAX_RETRIES && dataset?.status_name !== "DONE")) {
            toast("There was an error evaluating the selected KPI.", {
                type: toast.TYPE.ERROR,
                position: toast.POSITION.TOP_CENTER,
                autoClose: 7500
            });
            return Promise.resolve(undefined);
        }
        if (dataset?.status_name !== "DONE") {
            let newRetries = retries;
            if (dataset?.status_name !== "RUNNING") {
                newRetries = retries + 1;
            }
            return delay(5000).then(() => dispatch(awaitDatasetProcess(datasetId, newRetries, maxRetries)));
        }
        return dispatch(fetchDataset(datasetId, undefined, true));
    };
}
export function fetchDataset(datasetId, skipUpdate = false, skipFetchIfHasDetails = false) {
    return (dispatch, getState) => {
        const dataset = getDataset(getState(), datasetId);
        if (isNaN(datasetId)) {
            return Promise.resolve(dataset);
        }
        if (skipUpdate) {
            return DatasetAPI.getDataset(datasetId).then((d) => transformDataset(d));
        }
        const datasetFetching = {
            ...globalReducer.getIsDatasetFetching(getState())
        };
        if (datasetFetching[datasetId] === true) {
            return Promise.resolve(undefined);
        }
        const promises = [];
        if (dataset === undefined) {
            datasetFetching[datasetId] = true;
            dispatch(globalReducer.updateIsDatasetFetching(datasetFetching));
            promises.push(DatasetAPI.getDataset(datasetId)
                .then((response) => {
                dispatch(updateDataset(response.id, transformDataset(response)));
                const retDatasetFetching = {
                    ...globalReducer.getIsDatasetFetching(getState())
                };
                retDatasetFetching[datasetId] = undefined;
                dispatch(globalReducer.updateIsDatasetFetching(retDatasetFetching));
                dispatch(updateTags(response.id, []));
                return Promise.allSettled([
                    dispatch(fetchComments(response.id)),
                    dispatch(fetchRatings(response.id)),
                    dispatch(fetchTags(response.id))
                ]);
            })
                .then(() => getDataset(getState(), datasetId))
                .catch((e) => {
                console.log(e);
                throw e;
            }));
        }
        else if (!skipFetchIfHasDetails) {
            promises.push(dispatch(fetchDatasetFeedback(datasetId)));
        }
        return Promise.allSettled(promises)
            .then(() => {
            if (getPivotTables(getState(), datasetId) === undefined) {
                return dispatch(fetchPivotTables(datasetId)).catch((e) => {
                    console.log(e);
                    throw e;
                });
            }
        })
            .then(() => {
            return getDataset(getState(), datasetId);
        });
    };
}
export function fetchPivotTables(datasetId) {
    return (dispatch, getState) => {
        const dataset = getDataset(getState(), datasetId);
        if (dataset === undefined) {
            return Promise.resolve(undefined);
        }
        return AibleClientFactory.getInstance()
            .getAccountClient()
            .getCustomerClientFactory(dataset.account_id)
            .then((factory) => {
            return factory
                .getMetaClient()
                .getDatasetDetail(dataset.id)
                .then((details) => {
                if (details.pivot_tables === undefined) {
                    return factory.getDatasetClient().getDatasetPivotTables(dataset.id);
                }
                if (details.pivot_tables.tables === undefined || details.pivot_tables.tables.length == 0) {
                    return factory
                        .getDatasetClient()
                        .getDatasetPivotTablesSubset(dataset.id, { max_order: 2 })
                        .then((tables) => {
                        return Promise.resolve({
                            ...details.pivot_tables,
                            tables: tables
                        });
                    });
                }
                return details.pivot_tables;
            })
                .catch((e) => {
                console.log(e);
                throw e;
            });
        })
            .then((pivotTables) => {
            if (pivotTables !== undefined) {
                dispatch(updatePivotTables(datasetId, pivotTables));
            }
            return pivotTables;
        })
            .catch((e) => {
            console.log(e);
            throw e;
        });
    };
}
export function fetchDatasetFeedback(datasetId, skipTags = true, forceFetch = false) {
    return (dispatch, getState) => {
        const dataset = getDataset(getState(), datasetId);
        if (dataset === undefined) {
            return dispatch(fetchDataset(datasetId));
        }
        const hasComments = getCommentCount(getState(), datasetId) === getComments(getState(), datasetId).length;
        const hasRatings = getPositiveRatingCount(getState(), datasetId) + getNegativeRatingCount(getState(), datasetId) ===
            getRatings(getState(), datasetId).length;
        const hasTags = skipTags || Boolean(dataset.tags && dataset.tags.length);
        if (hasTags === false) {
            dispatch(updateTags(datasetId, []));
        }
        return Promise.all([
            (forceFetch || !hasComments) && dispatch(fetchComments(datasetId)),
            (forceFetch || !hasRatings) && dispatch(fetchRatings(datasetId)),
            (forceFetch || !hasTags) && dispatch(fetchTags(datasetId))
        ]).then(() => {
            return getDataset(getState(), datasetId);
        });
    };
}
export function updateColumnDescription(datasetId, index, column, description) {
    return (dispatch, getState) => {
        const descriptions = (getDescriptions(getState(), datasetId) ?? []).slice();
        if (descriptions) {
            descriptions[index] = description;
            dispatch({
                type: UPDATE_FIELD_VALUE,
                id: datasetId,
                key: "descriptions",
                data: descriptions
            });
            return DatasetAPI.updateColumnDescriptions(datasetId, descriptions);
        }
        return Promise.resolve();
    };
}
export function changeTimestampColumn(datasetId, timestampCol) {
    return (dispatch, getState) => {
        const retOptions = {
            ...(getDataset(getState(), datasetId)?.options ?? {}),
            timestamp_column: timestampCol
        };
        return DatasetAPI.updateOptions(datasetId, retOptions).then((response) => dispatch(updateDataset(response.id, transformDataset(response))));
    };
}
export function updateTargetValue(datasetId, targetValue) {
    return (dispatch) => {
        return DatasetAPI.updateTargetValue(datasetId, targetValue).then((response) => dispatch(updateDataset(response.id, transformDataset(response))));
    };
}
export function copyAndChangeDatasetSuccess(datasetId, successValue) {
    return (dispatch, getState) => {
        const dataset = getDataset(getState(), datasetId);
        if (dataset === undefined) {
            return Promise.resolve(dataset);
        }
        return DatasetAPI.copyAndTransform(dataset, undefined, undefined, undefined, successValue).then((response) => {
            dispatch(updateDataset(response.id, transformDataset(response)));
        });
    };
}
export function copyAndChangeDatasetTarget(datasetId, targetValue, successValue) {
    return async (dispatch, getState) => {
        const dataset = getDataset(getState(), datasetId);
        if (dataset === undefined) {
            return Promise.resolve(dataset);
        }
        const newDataset = await DatasetAPI.copyAndTransform(dataset, undefined, undefined, targetValue, successValue);
        dispatch(updateDataset(newDataset?.id, transformDataset(newDataset)));
        // Fetch the original dataset to reset state of the original dataset without data tab mutations
        dispatch(fetchDataset(datasetId));
        // Return the new dataset so that we can use it when creating a copy from outcome selection in DatasetSelector component
        return getDataset(getState(), newDataset?.id);
    };
}
export function updateTransformations(datasetId, transformations) {
    return (dispatch) => {
        dispatch({
            type: UPDATE_FIELD_VALUE,
            id: datasetId,
            key: "transformations",
            data: transformations
        });
        return DatasetAPI.updateTransformations(datasetId, transformations).then((res) => dispatch(updateDataset(datasetId, transformDataset(res))));
    };
}
export function updatePendingTransformations(datasetId, transformations) {
    return (dispatch, getState) => {
        const pending = getDataset(getState(), datasetId)?.pendingTransformations;
        dispatch({
            type: UPDATE_FIELD_VALUE,
            id: datasetId,
            key: "pendingTransformations",
            data: {
                ...pending,
                ...transformations
            }
        });
    };
}
export function applyTransformations(datasetId, transformations) {
    return (dispatch) => {
        return DatasetAPI.updateTransformations(datasetId, transformations).then((res) => dispatch(updateDataset(res.id, transformDataset(res))));
    };
}
export function copyTransformFromSetup(datasetId, jobId = "new") {
    return (dispatch, getState) => {
        const ignored = { ...setupReducer.getIgnored(getState(), jobId) };
        const transformations = {
            ...getDataset(getState(), datasetId)?.transformations
        };
        if (ignored) {
            (transformations?.exclude_columns ?? []).forEach((col) => {
                ignored[col] = true;
            });
            transformations.exclude_columns = Object.keys(ignored).filter((k) => ignored[k] === true);
        }
        return dispatch(copyAndApplyTransformations(datasetId, transformations));
    };
}
export function copyAndUpdate(datasetId, updates) {
    return (dispatch, getState) => {
        const dataset = getDataset(getState(), datasetId);
        if (dataset === undefined) {
            return Promise.resolve(dataset);
        }
        return DatasetAPI.copyAndUpdate(dataset, updates).then((response) => {
            dispatch(updateDataset(response.id, transformDataset(response)));
            //dispatch(nextReducer.init("new", response.id));
            dispatch(viewReducer.updateType("datasets"));
            dispatch(push("/"));
            setTimeout(() => {
                const datasetDiv = document.getElementById("dataset_" + response?.id);
                if (datasetDiv) {
                    datasetDiv.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                        inline: "center"
                    });
                }
            }, 1000);
            return getDataset(getState(), response.id);
        });
    };
}
export function copyAndApplyTransformations(datasetId, transformations, updatedName, outcome, outcomeSuccess) {
    return (dispatch, getState) => {
        let dataset = getDataset(getState(), datasetId);
        if (dataset === undefined) {
            return Promise.resolve(dataset);
        }
        dataset = { ...dataset };
        if (dataset.target != outcome) {
            dataset.target = outcome;
        }
        if ((dataset?.target_value === null || dataset?.target_value?.[0] === undefined) && outcomeSuccess !== undefined) {
            dataset.target_value = [outcomeSuccess];
        }
        console.log("ccopy and apply transformationsss transformations", transformations, dataset, outcome, outcomeSuccess);
        return DatasetAPI.copyAndTransform(dataset, transformations, updatedName, outcome, outcomeSuccess).then((response) => {
            dispatch(updateDataset(response.id, transformDataset(response)));
            //dispatch(nextReducer.init("new", response.id));
            dispatch(viewReducer.updateType("datasets"));
            dispatch(push("/"));
            setTimeout(() => {
                const datasetDiv = document.getElementById("dataset_" + response?.id);
                if (datasetDiv) {
                    datasetDiv.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                        inline: "center"
                    });
                }
            }, 1000);
        });
    };
}
export function copyAndApplyPlan(datasetId, updatedName = undefined, outcome = undefined, outcomeSuccess = undefined) {
    return (dispatch, getState) => {
        let dataset = getDataset(getState(), datasetId);
        if (dataset === undefined) {
            return Promise.resolve(dataset);
        }
        dataset = { ...dataset };
        const transformations = { plan: dataset?.transformations?.plan };
        if (dataset?.target === null || dataset?.target === undefined) {
            dataset.target = outcome;
        }
        if ((dataset?.target_value === null || dataset?.target_value?.[0] === undefined) && outcomeSuccess !== undefined) {
            dataset.target_value = [outcomeSuccess];
        }
        return DatasetAPI.copyAndTransform(dataset, transformations, updatedName, outcome, outcomeSuccess).then((response) => {
            dispatch(updateDataset(response.id, transformDataset(response)));
            //dispatch(nextReducer.init("new", response.id));
            dispatch(viewReducer.updateType("datasets"));
            dispatch(push("/"));
            setTimeout(() => {
                const datasetDiv = document.getElementById("dataset_" + response?.id);
                if (datasetDiv) {
                    datasetDiv.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                        inline: "center"
                    });
                }
            }, 1000);
        });
    };
}
export function linkDataset(settings) {
    return (dispatch, getState) => {
        const { uploadName, ...file } = settings;
        const folderId = foldersReducer.getSelectedFolder(getState());
        const accountId = accountsReducer.getSelectedAccountId(getState());
        return DatasetAPI.linkDataset(file, file.name ?? `New ${settings.type ?? ""} dataset`, folderId, accountId, uploadName).then((response) => {
            return dispatch(updateDataset(response.id, transformDataset(response)));
        });
    };
}
export function pollDatasets() {
    return (dispatch, getState) => {
        const datasets = getProcessingDatasets(getState());
        for (const dataset of datasets) {
            DatasetAPI.getDataset(dataset.id).then((response) => {
                return dispatch(updateDataset(response.id, transformDataset(response, true)));
            });
        }
    };
}
export function pollProcessingDataset(datasetId) {
    return (dispatch) => {
        return DatasetAPI.getDataset(datasetId).then((response) => {
            const retResponse = response;
            let dataset = undefined;
            try {
                dataset = transformDataset(retResponse, true);
            }
            catch (e) {
                console.log(e);
                return Promise.resolve(dataset);
            }
            dispatch(updateDataset(retResponse.id, dataset));
            const isProcessing = dataset.rowCount === -1 &&
                dataset.errors.length === 0 &&
                dataset.status &&
                (dataset.status.indexOf("missing_target_variable") === -1 ||
                    dataset.status.indexOf("not_indexed") === -1 ||
                    dataset.dataset_status !== "DONE");
            if (isProcessing) {
                return delay(5000).then(() => dispatch(pollProcessingDataset(datasetId)));
            }
            return Promise.resolve(dataset);
        });
    };
}
export function deleteDataset(id) {
    return (dispatch, getState) => {
        const dataset = getDataset(getState(), id);
        const selectedAccountType = getSelectedAccountType(getState()) || "cloud";
        const shouldDelete = confirm("After removing this card Aible will not be able to run models with the Dataset: " +
            (dataset ? dataset.label : id) +
            " unless you re-upload the data. Aible cannot delete datasets from your " +
            selectedAccountType +
            " account(s), " +
            "so this action will not delete the actual dataset. If you wish to remove the dataset, " +
            "you will need to delete it from your " +
            selectedAccountType +
            " account.");
        if (shouldDelete) {
            return DatasetAPI.deleteDataset(id)
                .then((response) => {
                return dispatch(updateDataset(response.id, transformDataset(response)));
            })
                .catch(() => dispatch(updateError("Deletion failed since Projects still exist which reference this Dataset. " +
                "You must first delete the Projects created from this Dataset.")));
        }
    };
}
export function filterFetchedDatasets(state, data) {
    let filteredData = getIsMarketplace() ? filterOtherUsers(data, state) : data;
    filteredData = filteredData.filter((fData) => {
        const current = getDataset(state, fData.id);
        return current === undefined || current.updated_date != fData.updated_date;
    });
    return filteredData;
}
// TODO: Bad long call fetches from every folder
export function fetchAllDatasets() {
    return (dispatch, getState) => {
        dispatch(updateIsDatasetsLoading(true));
        //const folderId = foldersReducer.getSelectedFolder(getState());
        return DatasetAPI.getDatasets({
            next: globalReducer.getDatasetOffset(getState()),
            fetchAll: true
        }).then((data) => {
            //const filteredData = filterFetchedDatasets(getState(), data);
            dispatch(updateDatasets(transformDatasets(getState(), data)));
            if (getIsDatasetsLoading(getState())) {
                dispatch(updateIsDatasetsLoading(false));
            }
        });
    };
}
export function fetchNextDatasets(fetchAll, ignoreLatest, skipUpdate, isConsumer, checkShowMore) {
    return (dispatch, getState) => {
        if (!skipUpdate && !getIsDatasetsLoading(getState())) {
            dispatch(updateIsDatasetsLoading(true));
        }
        const latestDatasetId = paginationReducer.getDatasetsPage(getState());
        const folderId = foldersReducer.getSelectedFolder(getState());
        return DatasetAPI.getDatasets({
            folderId: isConsumer ? undefined : folderId,
            next: globalReducer.getDatasetOffset(getState()),
            latestId: ignoreLatest ? undefined : latestDatasetId,
            fetchAll: fetchAll
        }).then((data) => {
            if (folderId !== foldersReducer.getSelectedFolder(getState())) {
                return {};
            }
            dispatch(paginationReducer.storeLatestDataset(data));
            const filteredData = filterFetchedDatasets(getState(), data);
            const transformedDatasets = transformDatasets(getState(), filteredData);
            if (skipUpdate) {
                return transformedDatasets;
            }
            dispatch(updateDatasets(transformedDatasets));
            // Hide the showmore button if we don't see any more datasets
            if (checkShowMore && !Object.values(filteredData).length) {
                dispatch(paginationReducer.updateHideShowMoreDatasets(true));
            }
            if (getIsDatasetsLoading(getState())) {
                dispatch(updateIsDatasetsLoading(false));
            }
            return transformedDatasets;
        });
    };
}
export function fetchPinnedDatasets() {
    return (dispatch, getState) => {
        let pinnedDatasets = {};
        if (localStorage) {
            const storedPinnedDatasets = loginReducer.getItem(getState(), "sensePinnedDatasets");
            if (storedPinnedDatasets !== undefined && storedPinnedDatasets !== null) {
                pinnedDatasets = JSON.parse(storedPinnedDatasets);
            }
        }
        const pinnedIds = keysOf(pinnedDatasets).filter((dId) => {
            const currDataset = getDataset(getState(), dId);
            if (isNaN(dId)) {
                return false;
            }
            return currDataset?.id === undefined;
        });
        if (!pinnedIds?.length) {
            return Promise.resolve();
        }
        const folderId = foldersReducer.getSelectedFolder(getState());
        return DatasetAPI.getDatasetsById(pinnedIds, folderId).then((data) => {
            if (folderId !== foldersReducer.getSelectedFolder(getState())) {
                return;
            }
            const filteredData = filterFetchedDatasets(getState(), data);
            dispatch(updateDatasets(transformDatasets(getState(), filteredData)));
        });
    };
}
export function changeDatasetTarget(id, target, runId) {
    return (dispatch, getState) => {
        const dataset = getDataset(getState(), id);
        return DatasetAPI.setNewTarget(id, target, dataset?.options).then((res) => {
            dispatch(updateDataset(id, transformDataset(res)));
            if (runId) {
                dispatch(setupReducer.updateSuccess(runId, undefined));
            }
            return Promise.resolve();
        });
    };
}
export function setDatasetTarget(id, target, success) {
    return (dispatch, getState) => {
        const dataset = getDataset(getState(), id);
        return DatasetAPI.setTarget(id, target, success, dataset?.options).then((res) => {
            dispatch(updateDataset(id, transformDataset(res)));
        });
    };
}
export function updateDatasetOptions(id, options) {
    return { type: UPDATE_FIELD_VALUE, id: id, key: "options", data: options };
}
export function init() {
    return (dispatch) => {
        return dispatch(initLogin());
    };
}
export default homeReducer;
export function getTargetValue(state, id) {
    if (getOutcomeType(state, id) === "number") {
        return undefined;
    }
    return getDataset(state, id)?.target_value?.[0];
}
export function getTarget(state, id) {
    return getDataset(state, id)?.target;
}
// User Feedback
export function getCommentCount(state, id) {
    const dataset = getDataset(state, id);
    return dataset && dataset.comment_count ? dataset.comment_count : 0;
}
export function getComments(state, id) {
    const dataset = getDataset(state, id);
    return dataset && dataset.comments ? dataset.comments : [];
}
export function getPositiveRatingCount(state, id) {
    const dataset = getDataset(state, id);
    return dataset && dataset.like_count ? dataset.like_count : 0;
}
export function getNegativeRatingCount(state, id) {
    const dataset = getDataset(state, id);
    return dataset && dataset.dislike_count ? dataset.dislike_count : 0;
}
export function getRatings(state, id) {
    const dataset = getDataset(state, id);
    return dataset && dataset.ratings ? dataset.ratings : [];
}
export function getTags(state, id) {
    const dataset = getDataset(state, id);
    return dataset && dataset.tags ? dataset.tags : [];
}
export function getTagCount(state, id) {
    const dataset = getDataset(state, id);
    return dataset && dataset.tags ? dataset.tags.length : 0;
}
export function getPositiveRatings(state, id) {
    return getRatings(state, id).filter((rating) => rating.score === "LIKE");
}
export function getNegativeRatings(state, id) {
    return getRatings(state, id).filter((rating) => rating.score === "DISLIKE");
}
export function updateRatings(id, ratings) {
    return { type: UPDATE_FIELD_VALUE, id: id, key: "ratings", data: ratings };
}
export function updateComments(id, comments) {
    return (dispatch) => {
        dispatch({
            type: UPDATE_FIELD_VALUE,
            id: id,
            key: "comment_count",
            data: comments.length
        });
        return dispatch({
            type: UPDATE_FIELD_VALUE,
            id: id,
            key: "comments",
            data: comments
        });
    };
}
export function updateTags(id, tags) {
    return { type: UPDATE_FIELD_VALUE, id: id, key: "tags", data: tags };
}
export function updatePositiveRating(id, change) {
    return {
        type: UPDATE_FIELD_VALUE,
        id: id,
        key: "like_count",
        data: change
    };
}
export function updateNegativeRating(id, change) {
    return {
        type: UPDATE_FIELD_VALUE,
        id: id,
        key: "dislike_count",
        data: change
    };
}
export function getUserRating(state, id) {
    const userId = loginReducer.getUserID(state);
    const userRating = getRatings(state, id).find((rating) => rating.created_by && rating.created_by.id === userId);
    return userRating ? userRating.score : "NONE";
}
export function toggleRating(id, rating) {
    return (dispatch, getState) => {
        const state = getState();
        const currentRating = getUserRating(state, id);
        let ratingChange = rating;
        if (currentRating === rating) {
            ratingChange = "NONE";
        }
        return dispatch(rateDataset(id, ratingChange)).then(() => {
            const currNegative = getNegativeRatings(getState(), id);
            const currPositive = getPositiveRatings(getState(), id);
            dispatch({
                type: UPDATE_FIELD_VALUE,
                id: id,
                key: "dislike_count",
                data: currNegative ? currNegative.length : 0
            });
            dispatch({
                type: UPDATE_FIELD_VALUE,
                id: id,
                key: "like_count",
                data: currPositive ? currPositive.length : 0
            });
            return Promise.resolve({});
        });
    };
}
export function fetchRatings(id) {
    return (dispatch) => {
        return FeedbackAPI.getDatasetRatings(id).then((ratings) => dispatch(updateRatings(id, ratings)));
    };
}
export function fetchComments(id) {
    return (dispatch) => {
        return FeedbackAPI.getDatasetComments(id).then((comments) => dispatch(updateComments(id, comments)));
    };
}
export function fetchTags(id) {
    return (dispatch) => {
        return TagAPI.getDatasetTags(id).then((tags) => dispatch(updateTags(id, tags)));
    };
}
export function rateDataset(id, rating) {
    return (dispatch) => {
        return FeedbackAPI.rateDataset(id, rating).then(() => dispatch(fetchRatings(id)));
    };
}
export function postComment(id, comment) {
    return (dispatch) => {
        return FeedbackAPI.postDatasetComment(id, comment).then(() => dispatch(fetchComments(id)));
    };
}
export function deleteComment(datasetId, commentId) {
    return (dispatch) => {
        return FeedbackAPI.deleteComment(commentId).then(() => dispatch(fetchComments(datasetId)));
    };
}
export function postTag(id, tagName) {
    return (dispatch) => {
        if (tagName) {
            return TagAPI.addDatasetToTag(tagName.toLowerCase(), id).then(() => dispatch(fetchTags(id)));
        }
        else {
            return Promise.resolve({});
        }
    };
}
export function deleteTag(id, tagName) {
    return (dispatch) => {
        return TagAPI.deleteDatasetFromTag(tagName, id).then(() => dispatch(fetchTags(id)));
    };
}
export function fetchDatasetTags() {
    return () => {
        return TagAPI.getAllDatasetTags().then((tags) => console.log(tags));
    };
}
export function getIsLinkedDataset(state, id) {
    const dataset = getDataset(state, id);
    const content = dataset?.source?.content;
    return content?.type == "CONNECTION_METADATA";
}
export function getDatasetSourceDetails(state, id) {
    const dataset = getDataset(state, id);
    const dataSource = String(dataset && dataset.source && dataset.source.content && dataset.source.content.type == "CONNECTION_METADATA"
        ? dataset.source.content.provider
        : dataset &&
            dataset.source &&
            dataset.source.type &&
            dataset.source.type.provider_name &&
            (dataset.source.type.provider_name.toUpperCase() === "AMAZON" ||
                dataset.source.type.provider_name.toUpperCase() === "AZURE")
            ? dataset.source.type.provider_name.toUpperCase()
            : "CSV").toUpperCase();
    return findDataSourceDetails(dataSource);
}
export function uploadFile(state, branch, fileName, file, gitMsg = "Automatically updating Looker Model") {
    const lookerModelsRepoUUID = lookerSettingsReducer.getLookerModelsRepoUUID(state);
    const workspaceId = lookerSettingsReducer.getWorkspaceId(state);
    const bitbucketCreds = lookerSettingsReducer.getBitbucketCreds(state);
    const url = "https://api.bitbucket.org/2.0/repositories/" + workspaceId + "/" + lookerModelsRepoUUID + "/src";
    const formdata = new FormData();
    formdata.append("message", gitMsg);
    formdata.append("branch", branch);
    formdata.append(fileName, file);
    return fetch(url, {
        method: "POST",
        headers: { Authorization: "Basic " + bitbucketCreds, "Content-Type": "multipart/form-data" },
        body: formdata
    }).then((response) => response);
}
export function getModelFile(state, branch) {
    const lookerModelsRepoUUID = lookerSettingsReducer.getLookerModelsRepoUUID(state);
    const workspaceId = lookerSettingsReducer.getWorkspaceId(state);
    const bitbucketCreds = lookerSettingsReducer.getBitbucketCreds(state);
    const modelFile = lookerSettingsReducer.getLookerModelFile(state);
    const url = "https://api.bitbucket.org/2.0/repositories/" +
        workspaceId +
        "/" +
        lookerModelsRepoUUID +
        "/src/" +
        branch +
        "/" +
        modelFile;
    return fetch(url, {
        headers: { Authorization: "Basic " + bitbucketCreds }
    }).then((response) => response.text());
}
function buildViewColumn(outcome, schema) {
    let retText = undefined;
    const retName = schema.name !== undefined ? schema.name.replace(/ /g, "_") : schema.name;
    if (outcome === schema?.name) {
        retText =
            "\n  dimension: " + retName.toLowerCase() + " {\n    type: yesno\n    sql: ${TABLE}." + retName + " ;;\n  }\n";
    }
    else {
        retText =
            "\n  dimension: " + retName.toLowerCase() + " {\n    type: string\n    sql: ${TABLE}." + retName + " ;;\n  }\n";
    }
    /*
      else if (schema.type === "NUMERIC") {
          retText =
              "\n  dimension: " +
              schema.name.toLowerCase() +
              " {\n    type: number\n    sql: ${TABLE}." +
              schema.name +
              " ;;\n  }\n";
  
      } else if (schema.type === "DATE") {
          retText =
              "\n  dimension_group: " +
              schema.name.toLowerCase() +
              " {\n    type: time\n    timeframes: [\n      raw,\n      time,\n      " +
              "date,\n      week,\n      month,\n      quarter,\n      year\n    ]\n    sql: ${TABLE}." +
              schema.name +
              " ;;\n  }\n";
      }
      */
    return retText;
}
export function buildViewFile(state, datasetId, outcome) {
    const accountId = accountsReducer.getSelectedAccountId(state);
    const dataset = getDataset(state, datasetId);
    const retOutcome = outcome != undefined ? outcome : dataset?.outcome;
    const rows = [
        "view: aible_dataset_" + datasetId + "_binned {",
        "\n  sql_table_name: `aible_tmp_account_" + accountId + ".aible_dataset_" + datasetId + "_binned`",
        "\n    ;;\n"
    ];
    if (retOutcome !== undefined) {
        dataset?.source?.content?.schema?.forEach((col) => {
            const nextCol = buildViewColumn(retOutcome, col);
            if (nextCol !== undefined) {
                rows.push(nextCol);
            }
        });
    }
    rows.push("\n}");
    const file = new Blob(rows, { type: "text/plain" });
    console.log("what is file", file);
    return file;
}
export function checkUpdateLooker(datasetId, nextId, messageId = undefined) {
    return (dispatch, getState) => {
        const coreSDK = loginReducer.getLookerAPIClient(getState());
        const dataset = getDataset(getState(), datasetId);
        const outcome = dataset?.outcome;
        const lookerProjectName = lookerSettingsReducer.getLookerProjectName(getState());
        const modelFile = lookerSettingsReducer.getLookerModelFile(getState());
        return coreSDK.git_branch(lookerProjectName).then((r) => {
            const branch = r.value.name;
            if (r?.value?.is_production === true) {
                return toast("Please turn on Looker Development mode to use this feature!", {
                    type: toast.TYPE.WARNING,
                    position: toast.POSITION.TOP_CENTER
                });
            }
            return getModelFile(getState(), branch).then((response) => {
                // We need to update looker
                if (response.indexOf("explore: aible_dataset_" + datasetId + "_binned {}") === -1) {
                    console.log("need to update looker model");
                    const file = new Blob([response, "\nexplore: aible_dataset_" + datasetId + "_binned {}"], {
                        type: "text/plain"
                    });
                    return uploadFile(getState(), branch, "views/aible_dataset_" + datasetId + "_binned.view.lkml", buildViewFile(getState(), datasetId, outcome)).then(() => {
                        uploadFile(getState(), branch, modelFile, file, String(datasetId)).then(() => {
                            console.log("done updating dataset " + datasetId);
                            coreSDK.reset_project_to_remote(lookerProjectName).then(() => coreSDK.deploy_to_production(lookerProjectName).then(() => {
                                toast("Looker Model Automatically Updated!", {
                                    type: toast.TYPE.SUCCESS,
                                    position: toast.POSITION.TOP_CENTER
                                });
                                return dispatch(nextReducer.createDashboard(nextId, messageId));
                            }));
                        });
                    });
                }
                return dispatch(nextReducer.createDashboard(nextId, messageId));
            });
        });
    };
}
export function buildModelFile(state, datasetId) {
    const connectionName = lookerSettingsReducer.getConnectionName(state);
    const modelName = lookerSettingsReducer.getLookerModelName(state);
    const file = new Blob([
        'connection: "' +
            connectionName +
            '"\n' +
            'include: "/views/**/*.view"\n' +
            "datagroup: " +
            modelName +
            "_default_datagroup {\n" +
            'max_cache_age: "1 hour"\n' +
            "}\n" +
            "persist_with: " +
            modelName +
            "_default_datagroup\n" +
            "\nexplore: aible_dataset_" +
            datasetId +
            "_binned {}"
    ], {
        type: "text/plain"
    });
    return file;
}
export function updateLookerModel(datasetId, outcome) {
    return (dispatch, getState) => {
        const coreSDK = loginReducer.getLookerAPIClient(getState());
        const lookerProjectName = lookerSettingsReducer.getLookerProjectName(getState());
        const modelFile = lookerSettingsReducer.getLookerModelFile(getState());
        coreSDK.git_branch(lookerProjectName).then((r) => {
            const branch = r.value.name;
            return getModelFile(getState(), branch).then((response) => {
                if (response.indexOf("connection: ") === -1) {
                    const file = buildModelFile(getState(), datasetId);
                    uploadFile(getState(), branch, "views/aible_dataset_" + datasetId + "_binned.view.lkml", buildViewFile(getState(), datasetId, outcome)).then(() => {
                        uploadFile(getState(), branch, modelFile, file).then(() => {
                            console.log("done updating dataset " + datasetId);
                            coreSDK.reset_project_to_remote(lookerProjectName).then(() => coreSDK.deploy_to_production(lookerProjectName).then((r) => {
                                toast("Looker Model Automatically Updated!", {
                                    type: toast.TYPE.SUCCESS,
                                    position: toast.POSITION.TOP_CENTER
                                });
                                console.log("we deployed??", r);
                            }));
                        });
                    });
                }
                else if (response.indexOf("explore: aible_dataset_" + datasetId + "_binned {}") === -1) {
                    const file = new Blob([response, "\nexplore: aible_dataset_" + datasetId + "_binned {}"], {
                        type: "text/plain"
                    });
                    uploadFile(getState(), branch, "views/aible_dataset_" + datasetId + "_binned.view.lkml", buildViewFile(getState(), datasetId, outcome)).then(() => {
                        uploadFile(getState(), branch, modelFile, file).then(() => {
                            console.log("done updating dataset " + datasetId);
                            coreSDK.reset_project_to_remote(lookerProjectName).then(() => coreSDK.deploy_to_production(lookerProjectName).then((r) => {
                                toast("Looker Model Automatically Updated!", {
                                    type: toast.TYPE.SUCCESS,
                                    position: toast.POSITION.TOP_CENTER
                                });
                                console.log("we deployed??", r);
                            }));
                        });
                    });
                }
                else {
                    uploadFile(getState(), branch, "views/aible_dataset_" + datasetId + "_binned.view.lkml", buildViewFile(getState(), datasetId, outcome)).then(() => {
                        coreSDK.reset_project_to_remote(lookerProjectName).then(() => coreSDK.deploy_to_production(lookerProjectName).then((r) => {
                            toast("Looker Model Automatically Updated!", {
                                type: toast.TYPE.SUCCESS,
                                position: toast.POSITION.TOP_CENTER
                            });
                            console.log("we deployed??", r);
                        }));
                    });
                }
            });
        });
    };
}
/* KPI Analysis */
export function getKPIAnalysisSettings(state, id, datasetOverride) {
    const options = getDatasetOptionsSettings(state, id, datasetOverride);
    return options?.kpi_analysis;
}
export function getIsKPIAnalysis(state, id, datasetOverride) {
    const kpiSettings = getKPIAnalysisSettings(state, id, datasetOverride);
    return kpiSettings?.enable_kpi_analysis;
}
export function getKPIAnalysisDateColumn(state, id, datasetOverride) {
    const kpiSettings = getKPIAnalysisSettings(state, id, datasetOverride);
    return kpiSettings?.date_column;
}
export function getKPIAnalysisTimeBucket(state, id, datasetOverride) {
    const kpiSettings = getKPIAnalysisSettings(state, id, datasetOverride);
    return kpiSettings?.time_bucket;
}
export function getKPIAnalysisWeekStartSun(state, id, datasetOverride) {
    const kpiSettings = getKPIAnalysisSettings(state, id, datasetOverride);
    return kpiSettings?.week_is_sun_to_sat;
}
export function getKPIDateRange(state, id, datasetOverride) {
    const kpiSettings = getKPIAnalysisSettings(state, id, datasetOverride);
    return kpiSettings?.kpi_date_range;
}
export function getKPIDateFormat(state, id, datasetOverride) {
    const kpiSettings = getKPIAnalysisSettings(state, id, datasetOverride);
    return kpiSettings?.date_format;
}
export function getKPIDateRangeReferenceStart(state, id) {
    return getKPIDateRange(state, id)?.referenceStart;
}
export function getKPIDateRangeReferenceEnd(state, id) {
    return getKPIDateRange(state, id)?.referenceEnd;
}
export function getKPIDateRangeCurrentStart(state, id) {
    return getKPIDateRange(state, id)?.currentStart;
}
export function getKPIDateRangeCurrentEnd(state, id) {
    return getKPIDateRange(state, id)?.currentEnd;
}
// This indicates whether all the required fields for a KPI analysis have been selected
// We do not require the date ranges since there is default logic for those
export function getIsKPIReady(state, id) {
    const isKPIAnalysis = getIsKPIAnalysis(state, id);
    const KPIDateColumn = getKPIAnalysisDateColumn(state, id);
    const KPISegment = getKPIAnalysisSegment(state, id);
    return Boolean(isKPIAnalysis && KPISegment && KPIDateColumn);
}
// The number of combinations KPI analysis looks at
export function getKPICombinations(state, id) {
    // segment count * subsegment count * kpis
    const dataset = getDataset(state, id);
    if (!dataset) {
        return 0;
    }
    const segment = getKPIAnalysisSegment(state, id);
    const subsegment = getKPIAnalysisSubsegment(state, id);
    const excludeColumns = getKPIAnalysisExcludeColumns(state, id) ?? [];
    // cardinality of seg and subseg
    const segmentCount = dataset?.fields?.[segment ?? ""]?.data?.length || 0;
    const subsegmentCount = dataset?.fields?.[subsegment ?? ""]?.data?.length || 0;
    const kpiFields = Object.values(dataset?.fields ?? {})?.filter((f) => {
        if (!f) {
            return false;
        }
        if (f.label === segment || f.label === subsegment || excludeColumns.includes(f.label)) {
            return false;
        }
        if (f.type !== "number" && !f.isNumeric) {
            return false;
        }
        return true;
    }) ?? [];
    const combinationCount = kpiFields.length * (segmentCount * subsegmentCount);
    return combinationCount;
}
// get extrapolated date ranges
export function getKPIDateOptions(state, id) {
    const dataset = getDataset(state, id);
    const options = [];
    const dateColumn = getKPIAnalysisDateColumn(state, id);
    const timeBucket = getKPIAnalysisTimeBucket(state, id);
    const isSundayWeek = getKPIAnalysisWeekStartSun(state, id);
    // If user has selected date field and time range
    if (dateColumn && timeBucket) {
        const dateField = dataset?.fields?.[dateColumn];
        // make sure we have the data for the field
        if (dateField?.type == "date" && dateField?.data?.length) {
            const dateFormat = getKPIDateFormat(state, id);
            let minDate = moment(dateField?.min, dateFormat);
            let maxDate = moment(dateField?.max, dateFormat);
            // find the min and max dates if they're not precalculated
            if (!minDate || !maxDate) {
                dateField?.data?.forEach((d) => {
                    const v = moment(d.value, dateFormat);
                    if (!minDate || v.isBefore(minDate)) {
                        minDate = v;
                    }
                    if (!maxDate || v.isAfter(maxDate)) {
                        maxDate = v;
                    }
                });
            }
            // find the start date for generation;
            let startDate;
            if (timeBucket === "M") {
                const currMoment = minDate.clone().set("date", 1);
                startDate = currMoment;
            }
            else if (timeBucket === "W") {
                let currMoment = minDate.clone().isoWeekday(1);
                if (isSundayWeek) {
                    currMoment = currMoment.subtract(1, "day");
                }
                startDate = currMoment;
            }
            else if (timeBucket === "D") {
                const currMoment = minDate.clone();
                startDate = currMoment;
            }
            else if (timeBucket === "H") {
                // Currently Not supported
                return [];
            }
            else {
                // invalid date period;
                return [];
            }
            // future proofing for eventually handing hours
            const dateOutputFormat = "YYYY-MM-DD";
            // generate the list of dates
            const currDate = startDate;
            while (maxDate.isSameOrAfter(currDate)) {
                const displayDate = currDate.format(dateOutputFormat);
                options.push(displayDate);
                if (timeBucket === "M") {
                    currDate.add(1, "M");
                }
                else if (timeBucket === "W") {
                    currDate.add(1, "w");
                }
                else if (timeBucket === "D") {
                    currDate.add(1, "d");
                }
                else if (timeBucket === "H") {
                    // Currently Not supported
                    break;
                }
                else {
                    // invalid date period;
                    break;
                }
            }
        }
    }
    return options.map((o) => {
        return {
            label: o,
            value: o
        };
    });
}
export function getKPIAnalysisHierarchyColumns(state, id, datasetOverride) {
    const kpiSettings = getKPIAnalysisSettings(state, id, datasetOverride);
    return kpiSettings?.hierarchy_columns;
}
export function getKPIAnalysisSegment(state, id, datasetOverride) {
    const kpiHierarchy = getKPIAnalysisHierarchyColumns(state, id, datasetOverride);
    return kpiHierarchy?.[0];
}
export function getKPIAnalysisSubsegment(state, id, datasetOverride) {
    const kpiHierarchy = getKPIAnalysisHierarchyColumns(state, id, datasetOverride);
    return kpiHierarchy?.[1];
}
export function getKPIAnalysisExcludeColumns(state, id, datasetOverride) {
    const kpiSettings = getKPIAnalysisSettings(state, id, datasetOverride);
    return kpiSettings?.exclude_columns;
}
export function getKPIColumns(state, id, includeExcludedColumns = false) {
    const fields = getFieldsArr(state, id);
    const dateColumn = getKPIAnalysisDateColumn(state, id);
    const hierarchyColumns = getKPIAnalysisHierarchyColumns(state, id);
    const excludeColumns = getKPIAnalysisExcludeColumns(state, id);
    const retFields = fields.filter((f) => {
        if (!f) {
            return false;
        }
        const cField = f.label;
        if (cField === dateColumn) {
            return false;
        }
        if (hierarchyColumns?.includes(cField)) {
            return false;
        }
        if (!includeExcludedColumns && excludeColumns?.includes(cField)) {
            return false;
        }
        if (f.type !== "number" && !f.isNumeric) {
            return false;
        }
        return true;
    });
    return retFields.map((f) => f.label);
}
export function getKPIAnalysisPinnedCharts(state, id, datasetOverride) {
    const kpiSettings = getKPIAnalysisSettings(state, id, datasetOverride);
    return kpiSettings?.pinned_charts ?? [];
}
// Whether the column is displayed as AVERAGE or SUM
// default is SUM
export function getKPIAnalysisColumnCalcModes(state, id, datasetOverride) {
    const kpiSettings = getKPIAnalysisSettings(state, id, datasetOverride);
    return kpiSettings?.column_calc_mode ?? {};
}
export function getKPIAnalysisColumnCalcModeForKPI(state, id, column, datasetOverride) {
    const calcModes = getKPIAnalysisColumnCalcModes(state, id, datasetOverride);
    return calcModes[column] ?? "SUM";
}
export function getKPIAnalysisSumColumns(state, id) {
    const cols = getKPIColumns(state, id);
    const retCols = cols.filter((c) => {
        const calcMode = getKPIAnalysisColumnCalcModeForKPI(state, id, c);
        return calcMode === "SUM" || calcMode === "BOTH";
    });
    return retCols;
}
export function getKPIAnalysisAverageColumns(state, id) {
    const cols = getKPIColumns(state, id);
    const retCols = cols.filter((c) => {
        const calcMode = getKPIAnalysisColumnCalcModeForKPI(state, id, c);
        return calcMode === "AVERAGE" || calcMode === "BOTH";
    });
    return retCols;
}
export function getKPIAnalysisDeepDiveExcludeColumnOptions(state, id) {
    const fields = getFieldsArr(state, id);
    const dateColumn = getKPIAnalysisDateColumn(state, id);
    const retFields = fields.filter((f) => {
        if (!f) {
            return false;
        }
        const cField = f.label;
        if (cField === dateColumn) {
            return false;
        }
        return true;
    });
    return retFields.map((f) => f.label);
}
export function getKPIAnalysisDeepDiveExcludeColumns(state, id, datasetOverride) {
    const kpiSettings = getKPIAnalysisSettings(state, id, datasetOverride);
    const savedCols = kpiSettings?.deep_dive_exclude_columns;
    if (savedCols) {
        return savedCols;
    }
    const hierarchyColumns = getKPIAnalysisHierarchyColumns(state, id);
    const options = getKPIAnalysisDeepDiveExcludeColumnOptions(state, id);
    const dataset = getDataset(state, id);
    const defaultExclude = options.filter((kField) => {
        const field = dataset?.fields?.[kField];
        if (!field) {
            return false;
        }
        if (hierarchyColumns?.includes(kField)) {
            return false;
        }
        if (field.type === "number" || field.isNumeric || field.type === "date") {
            return true;
        }
        return false;
    });
    return defaultExclude;
}
export function updateKPISetting(id, key, data) {
    return (dispatch, getState) => {
        const kpiSettings = getKPIAnalysisSettings(getState(), id) ?? {};
        const retSettings = {
            ...kpiSettings,
            [key]: data
        };
        return dispatch(updateDatasetOptionsSettings(id, "kpi_analysis", retSettings));
    };
}
export function updateIsKPIAnalaysis(id, data) {
    return (dispatch) => {
        return dispatch(updateKPISetting(id, "enable_kpi_analysis", data));
    };
}
export function updateKPIAnalysisDateColumn(id, data) {
    return (dispatch) => {
        return dispatch(updateKPISetting(id, "date_column", data));
    };
}
export function updateKPIAnalysisTimeBucket(id, data) {
    return (dispatch) => {
        return dispatch(updateKPISetting(id, "time_bucket", data));
    };
}
export function updateKPIAnalysisWeekStartSun(id, data) {
    return (dispatch) => {
        return dispatch(updateKPISetting(id, "week_is_sun_to_sat", data));
    };
}
export function updateKPIDateFormat(id, data) {
    return (dispatch) => {
        return dispatch(updateKPISetting(id, "date_format", data));
    };
}
export function updateKPIDateRange(id, data) {
    return (dispatch) => {
        return dispatch(updateKPISetting(id, "kpi_date_range", data));
    };
}
export function updateKPIDateRangeValue(id, key, data) {
    return (dispatch, getState) => {
        const dateRange = getKPIDateRange(getState(), id) ?? {};
        return dispatch(updateKPIDateRange(id, {
            ...dateRange,
            [key]: data
        }));
    };
}
export function updateKPIAnalysisHierarchyColumns(id, data) {
    return (dispatch) => {
        const retData = data?.filter((o) => o !== undefined && o !== null);
        return dispatch(updateKPISetting(id, "hierarchy_columns", retData));
    };
}
export function updateKPIAnalysisSegment(id, data) {
    return (dispatch, getState) => {
        const subsegment = getKPIAnalysisSubsegment(getState(), id);
        const hierarchyColumns = [data, subsegment].filter((x) => x !== undefined);
        return dispatch(updateKPIAnalysisHierarchyColumns(id, hierarchyColumns));
    };
}
export function updateKPIAnalysisSubsegment(id, data) {
    return (dispatch, getState) => {
        const segment = getKPIAnalysisSegment(getState(), id);
        const hierarchyColumns = [segment, data].filter((x) => x !== undefined);
        return dispatch(updateKPIAnalysisHierarchyColumns(id, hierarchyColumns));
    };
}
export function updateKPIAnalysisExcludeColumns(id, data) {
    return (dispatch) => {
        return dispatch(updateKPISetting(id, "exclude_columns", data));
    };
}
export function updateKPIAnalysisPinnedCharts(id, data) {
    return (dispatch) => {
        return dispatch(updateKPISetting(id, "pinned_charts", data));
    };
}
export function updateKPIAnalysisColumnCalcModes(id, data) {
    return (dispatch) => {
        return dispatch(updateKPISetting(id, "column_calc_mode", data));
    };
}
export function updateKPIAnalysisColumnCalcModeByKPI(id, key, data) {
    return (dispatch, getState) => {
        const calcModes = getKPIAnalysisColumnCalcModes(getState(), id);
        const retCalcModes = {
            ...calcModes
        };
        if (data === undefined) {
            delete retCalcModes[key];
        }
        else {
            retCalcModes[key] = data;
        }
        return dispatch(updateKPIAnalysisColumnCalcModes(id, retCalcModes));
    };
}
export function updateKPIDeepDiveExcludeColumns(id, data) {
    return (dispatch) => {
        return dispatch(updateKPISetting(id, "deep_dive_exclude_columns", data));
    };
}
export function saveDatasetOptions(id, options, reindex = true) {
    return (dispatch) => {
        return DatasetAPI.updateOptions(id, options, reindex).then((response) => {
            if (response) {
                return dispatch(updateDataset(response?.id, transformDataset(response)));
            }
        });
    };
}
export function getIsSavingKPIAnalysis(state, id) {
    const dataset = getDataset(state, id);
    return dataset?.isSavingKPISettings || false;
}
export function saveKPISettings(id, timestampColumnOverride) {
    return (dispatch, getState) => {
        const options = getDatasetOptionsSettings(getState(), id);
        // Clean options settings;
        const retOptions = {
            ...options
        };
        if (retOptions?.kpi_analysis) {
            // add the default exclude columns shown
            if (!retOptions?.kpi_analysis?.deep_dive_exclude_columns) {
                const defaultExcludeColumns = getKPIAnalysisDeepDiveExcludeColumns(getState(), id);
                retOptions.kpi_analysis.deep_dive_exclude_columns = defaultExcludeColumns;
            }
            if (retOptions?.kpi_analysis?.hierarchy_columns) {
                const retCols = retOptions.kpi_analysis.hierarchy_columns.filter((o) => o !== undefined && o !== null);
                if (retCols?.length !== retOptions.kpi_analysis.hierarchy_columns.length) {
                    retOptions.kpi_analysis.hierarchy_columns = retCols;
                }
            }
            // WHY THE HELL IS THE DEFAULT TRUE IF THIS PROP ISN'T SENT
            if (!retOptions?.kpi_analysis.week_is_sun_to_sat) {
                retOptions.kpi_analysis.week_is_sun_to_sat = false;
            }
            if (retOptions?.kpi_analysis?.date_column && !retOptions?.timestamp_column) {
                retOptions.timestamp_column = retOptions.kpi_analysis.date_column;
            }
            if (timestampColumnOverride) {
                retOptions.timestamp_column = timestampColumnOverride;
            }
        }
        // Trying out not removing fields that were explicitly added
        // const calcModes = retOptions?.kpi_analysis?.column_calc_mode;
        // if (calcModes) {
        // 	const retCalcModes = {};
        // 	Object.keys(calcModes).forEach(k => {
        // 		if (calcModes[k] === "SUM" || !calcModes[k]) {
        // 			// We don't need to save the default calc
        // 			return;
        // 		}
        // 		retCalcModes[k] = calcModes[k]
        // 	})
        // 	retOptions.kpi_analysis.column_calc_mode = retCalcModes;
        // }
        if (!retOptions?.timestamp_column && options?.kpi_analysis?.date_column) {
            retOptions.timestamp_column = options?.kpi_analysis?.date_column;
        }
        return dispatch(saveDatasetOptions(id, retOptions));
    };
}
export function getDatasetSavedComparisonOptions(state, datasetId) {
    const dataset = getDataset(state, datasetId);
    const savedComparisonOptions = {
        currentEnd: dataset?.options?.kpi_analysis?.kpi_date_range?.currentEnd,
        currentStart: dataset?.options?.kpi_analysis?.kpi_date_range?.currentStart,
        dateField: dataset?.options?.timestamp_column ?? dataset?.options?.kpi_analysis?.date_column,
        referenceEnd: dataset?.options?.kpi_analysis?.kpi_date_range?.referenceEnd,
        referenceStart: dataset?.options?.kpi_analysis?.kpi_date_range?.referenceStart
    };
    let fields = dataset?.ranked_fields;
    if (!fields && dataset?.fields) {
        fields = Object.keys(dataset.fields).map((fKey) => dataset.fields[fKey]);
    }
    const dates = (fields ?? []).find((f) => f.label === savedComparisonOptions?.dateField)?.data;
    const filteredDates = (dates ?? [])?.filter((d) => d?.value !== "{Missing}");
    const checkIsMissingDateOption = (datesArr) => {
        const isMissing = datesArr.some((date) => {
            if (date === undefined) {
                return true;
            }
            const foundDate = filteredDates?.find((fDate) => fDate?.value === date);
            if (!foundDate) {
                return true;
            }
            return false;
        });
        return isMissing;
    };
    const comparisonDates = [
        savedComparisonOptions?.currentEnd,
        savedComparisonOptions?.currentStart,
        savedComparisonOptions?.referenceEnd,
        savedComparisonOptions?.referenceStart
    ];
    const missingDateRange = checkIsMissingDateOption(comparisonDates);
    if (savedComparisonOptions?.dateField && missingDateRange) {
        const datasetTimeBucket = dataset?.options?.kpi_analysis?.time_bucket;
        const { refStart, refEnd, currStart, currEnd } = chatGPTReducer.getDefaultDateIndices(filteredDates, datasetTimeBucket);
        savedComparisonOptions.referenceStart = filteredDates[refStart]?.value;
        savedComparisonOptions.referenceEnd = filteredDates[refEnd]?.value;
        savedComparisonOptions.currentStart = filteredDates[currStart]?.value;
        savedComparisonOptions.currentEnd = filteredDates[currEnd]?.value;
    }
    return savedComparisonOptions;
}
